import * as React from "react"

import { cn } from "@/lib/utils"

type CardElevation = "default" | "elevated" | "premium" | "secondary";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  elevation?: CardElevation;
}

const elevationStyles: Record<CardElevation, string> = {
  default: "bg-white border border-black/5 shadow-sm rounded-2xl",
  elevated: "bg-white border border-black/5 shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-md",
  premium: "bg-white border border-black/10 shadow-[0_24px_60px_-15px_rgba(0,0,0,0.08),0_0_0_1px_rgba(255,255,255,0.5)_inset] rounded-md",
  secondary: "bg-slate-50 border border-black/5 rounded-2xl"
};

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, elevation = "default", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "text-slate-950 transition-[transform,box-shadow,border-color] duration-300 relative overflow-hidden",
        elevationStyles[elevation],
        className
      )}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6 md:p-8", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn("font-bold tracking-tight text-2xl leading-none", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-slate-500 font-medium", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 md:p-8 pt-0 md:pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 md:p-8 pt-0 md:pt-0 gap-4", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
